import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportCategory } from '../../../models/Support/ISupportCategory';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketStatus } from '../../../models/Support/SupportEnums';
import type { InboxNavItem } from '../../../queries/Support/useSupportInboxNav';
import type { ISupportAgentConfig } from '../../../queries/Support/useSupportTeam';
import { usePrefetchSupportTicket } from '../../../queries/Support/useSupportTicketQuery';
import { getInitials, getSupportContactProfilePicSrc } from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { ChevronLeft } from '../../../web/components/svgs/icons/ChevronLeft';
import { Button } from '../../../web/components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { Pill } from '../../../web/components-v2/Pill';
import { countActiveInboxFilters, type InboxFilters, prepareInboxFilterChoices } from './inboxFilters';
import { SearchGlyph } from './inboxIcons';
import { inboxStyles as s } from './styles/inboxStyles';
import { formatRelativeTime, getStatusInfo, getTicketContactLabel } from './supportTicketPresentation';
import { TicketPagination } from './TicketPagination';
import { TicketSearchBar } from './TicketSearchBar';

/** The list of tickets plus its pagination, bundled so the pane takes one source instead of eight props. */
export interface TicketListSource {
	tickets: ISupportTicket[];
	isLoading: boolean;
	isError: boolean;
	totalCount: number;
	page: number;
	totalPages: number;
	onSelectPage: (page: number) => void;
}

interface TicketListPaneProps {
	/** Tickets + pagination for the active view (filter view or search results). */
	list: TicketListSource;
	viewLabel: string;
	onBack: () => void;
	selectedTicketId?: string;
	onSelectTicket: (id: string) => void;
	navItems: InboxNavItem[];
	categories: ISupportCategory[];
	activeFilter: string;
	onSelectFilter: (key: string) => void;
	/** Active while a search query is running — swaps the list to results and adjusts the count copy. */
	isSearching: boolean;
	searchValue: string;
	onSearchChange: (value: string) => void;
	/** Structured Status/Priority/Category/Assignee/Sort refinement from the Search criteria popover. */
	filters: InboxFilters;
	onFiltersChange: (next: InboxFilters) => void;
	onClearFilters: () => void;
	/** Mailbox agents, used to populate the Assignee filter. */
	teamMembers: ReadonlyArray<Pick<ISupportAgentConfig, 'agentId' | 'agentName' | 'agentProfilePic'>>;
	categoryMap: Record<string, { name: string; color?: string }>;
}

export const TicketListPane: React.FC<TicketListPaneProps> = ({
	list,
	viewLabel,
	onBack,
	selectedTicketId,
	onSelectTicket,
	navItems,
	categories,
	activeFilter,
	onSelectFilter,
	isSearching,
	searchValue,
	onSearchChange,
	filters,
	onFiltersChange,
	onClearFilters,
	teamMembers,
	categoryMap,
}) => {
	const { tickets, isLoading, isError, totalCount, page: currentPage, totalPages, onSelectPage } = list;
	const prefetchTicket = usePrefetchSupportTicket();

	const countNoun = activeFilter === 'closed' ? 'closed' : activeFilter === 'spam' ? 'in spam' : 'open';

	const filterChoices = React.useMemo(
		() => prepareInboxFilterChoices(categories, teamMembers),
		[categories, teamMembers]
	);

	// The search bar hides behind the header icon; it must stay visible while a refinement is
	// narrowing the list (including refinements set from the table view's header filters).
	const hasActiveRefinement = searchValue.trim().length > 0 || countActiveInboxFilters(filters) > 0;
	const [searchOpen, setSearchOpen] = React.useState(hasActiveRefinement);
	React.useEffect(() => {
		if (hasActiveRefinement) setSearchOpen(true);
	}, [hasActiveRefinement]);
	const toggleSearch = () => {
		if (searchOpen) {
			onSearchChange('');
			onClearFilters();
			setSearchOpen(false);
		} else {
			setSearchOpen(true);
		}
	};

	return (
		<section className={css(s.listPane)}>
			<div className={css(s.listHeader)}>
				<div className={css(s.listHeaderRow)}>
					<div className={css(s.listHeaderLeft)}>
						<Button type='button' variant='ghost' styles={[s.backBtn]} title='Back to inbox' onClick={onBack}>
							<ChevronLeft width={19} height={19} />
						</Button>
						<Dropdown>
							<DropdownTrigger render={<Button type='button' variant='ghost' styles={[s.listTitleBtn]} />}>
								<span className={css(s.listTitleGroup)}>
									<span className={css(s.listTitle)}>{viewLabel}</span>
									<ChevronDown className={css(s.switcherChevron)} width={14} height={14} strokeWidth={3} />
								</span>
							</DropdownTrigger>
							<DropdownContent align='start' styles={[s.viewMenu]}>
								{navItems.map(item => (
									<DropdownItem
										key={item.key}
										styles={activeFilter === item.key ? [s.viewMenuItem, s.viewMenuItemActive] : [s.viewMenuItem]}
										onClick={() => onSelectFilter(item.key)}
									>
										<span className={css(s.viewItemLabel)}>{item.label}</span>
										{item.count > 0 && <span className={css(s.viewCount)}>{item.count}</span>}
									</DropdownItem>
								))}
							</DropdownContent>
						</Dropdown>
					</div>
					<Button
						type='button'
						variant='ghost'
						styles={searchOpen ? [s.searchToggleBtn, s.searchToggleBtnActive] : [s.searchToggleBtn]}
						title={searchOpen ? 'Close search' : 'Search tickets'}
						aria-label={searchOpen ? 'Close search' : 'Search tickets'}
						aria-expanded={searchOpen}
						onClick={toggleSearch}
					>
						<SearchGlyph size={16} />
					</Button>
				</div>
				<span className={css(s.listSubCount)}>
					{isSearching ? `${totalCount} ${totalCount === 1 ? 'result' : 'results'}` : `${totalCount} ${countNoun}`}
				</span>
			</div>

			{searchOpen && (
				<TicketSearchBar
					value={searchValue}
					onChange={onSearchChange}
					filters={filters}
					onFiltersChange={onFiltersChange}
					categoryOptions={filterChoices.categoryOptions}
					assigneeOptions={filterChoices.assigneeOptions}
					allowSort={!isSearching}
					onClearAll={onClearFilters}
					autoFocus
				/>
			)}

			<div className={css(s.listScroll)}>
				{isLoading && (
					<div className={css(s.loading)}>
						<LoadingSpinner type='small' />
					</div>
				)}
				{!isLoading && isError && <div className={css(s.empty)}>Failed to load tickets.</div>}
				{!isLoading && !isError && tickets.length === 0 && (
					<div className={css(s.empty)}>
						{isSearching ? 'No tickets match your search.' : 'No tickets in this view.'}
					</div>
				)}
				{!isLoading &&
					!isError &&
					tickets.map(ticket => {
						const active = ticket.id === selectedTicketId;
						const status = getStatusInfo(ticket.status);
						const isPending = ticket.status === TicketStatus.Pending;
						const category = ticket.categoryId ? categoryMap[ticket.categoryId] : undefined;
						const contactLabel = getTicketContactLabel(ticket);
						return (
							<Button
								key={ticket.id}
								type='button'
								variant='ghost'
								styles={[
									s.ticketRow,
									...(active ? [s.ticketRowActive] : []),
									...(isPending ? [s.ticketRowPending] : []),
								]}
								aria-pressed={active}
								onClick={() => onSelectTicket(ticket.id)}
								onMouseEnter={() => prefetchTicket(ticket.id)}
								onFocus={() => prefetchTicket(ticket.id)}
							>
								<div className={css(s.rowTop)}>
									<Avatar
										imgSrc={getSupportContactProfilePicSrc(ticket)}
										altText={contactLabel === '—' ? 'Contact' : contactLabel}
										fallbackText={getInitials(ticket.contactName, ticket.contactEmail)}
										styles={[s.rowAvatar]}
									/>
									<span className={css(s.rowName)}>{contactLabel}</span>
									<span className={css(s.rowTime)}>{formatRelativeTime(ticket.lastMessageAt)}</span>
								</div>
								<div className={css(s.rowSubject)}>{ticket.subject}</div>
								{ticket.previewText && <div className={css(s.rowPreview)}>{ticket.previewText}</div>}
								<div className={css(s.rowPills)}>
									<Pill variant='tag' color={status.color} size='small' label={status.label} />
									{category && <Pill variant='category' color='gray' size='small' label={category.name} />}
								</div>
							</Button>
						);
					})}
			</div>
			<TicketPagination page={currentPage} totalPages={totalPages} onSelectPage={onSelectPage} />
		</section>
	);
};
